import type { BuzzFeedV1Block, BuzzFeedV1Item, BuzzFeedV1Response } from '@tark/shared/types';

export const BUZZ_MAX_BODY_PAGES = 5;
export const BUZZ_MAX_BODY_ITEMS = 50;

export function getBuzzContentIdentity(item: BuzzFeedV1Item): string | undefined {
  return normalizedText(item.contentKey) ?? normalizedText(item.itemId);
}

export function getBuzzBlockRenderKey(block: BuzzFeedV1Block): string {
  const serverBlockId = normalizedText(block.blockId);
  if (serverBlockId) {
    return serverBlockId;
  }

  const itemSignature = (block.items ?? [])
    .map(getBuzzContentIdentity)
    .filter((value): value is string => Boolean(value))
    .join('|');
  return `buzz-block:${blockGroupKey(block)}:${itemSignature || 'empty'}`;
}

export function getNextBoundedBuzzPageParam(
  lastPage: BuzzFeedV1Response,
  allPages: BuzzFeedV1Response[]
): string | undefined {
  if (
    allPages.length >= BUZZ_MAX_BODY_PAGES ||
    getBoundedBuzzBodyItemCount(allPages) >= BUZZ_MAX_BODY_ITEMS ||
    lastPage.pageInfo?.hasMore !== true
  ) {
    return undefined;
  }
  return normalizedText(lastPage.pageInfo?.nextCursor);
}

export function getBoundedBuzzBodyItemCount(pages: BuzzFeedV1Response[]): number {
  return buildBoundedBuzzBodyFromPages(pages).reduce(
    (total, block) => total + (block.items?.length ?? 0),
    0
  );
}

export function buildBoundedBuzzBodyFromPages(pages: BuzzFeedV1Response[]): BuzzFeedV1Block[] {
  const blocks = pages
    .slice(0, BUZZ_MAX_BODY_PAGES)
    .flatMap((page) => page.body ?? []);
  const seenItems = new Set<string>();
  const blockIndexByGroup = new Map<string, number>();
  const boundedBlocks: BuzzFeedV1Block[] = [];
  let retainedItemCount = 0;

  for (const block of blocks) {
    const uniqueItems: BuzzFeedV1Item[] = [];
    for (const item of block.items ?? []) {
      if (retainedItemCount >= BUZZ_MAX_BODY_ITEMS) {
        break;
      }

      const identity = getBuzzContentIdentity(item);
      if (identity && seenItems.has(identity)) {
        continue;
      }
      if (identity) {
        seenItems.add(identity);
      }
      uniqueItems.push(item);
      retainedItemCount += 1;
    }

    if (!uniqueItems.length) {
      if (retainedItemCount >= BUZZ_MAX_BODY_ITEMS) {
        break;
      }
      continue;
    }

    const groupKey = blockGroupKey(block);
    const existingIndex = blockIndexByGroup.get(groupKey);
    if (existingIndex === undefined) {
      blockIndexByGroup.set(groupKey, boundedBlocks.length);
      boundedBlocks.push({ ...block, items: uniqueItems });
    } else {
      const existing = boundedBlocks[existingIndex];
      boundedBlocks[existingIndex] = {
        ...existing,
        items: [...(existing.items ?? []), ...uniqueItems],
      };
    }

    if (retainedItemCount >= BUZZ_MAX_BODY_ITEMS) {
      break;
    }
  }

  return boundedBlocks;
}

function blockGroupKey(block: BuzzFeedV1Block): string {
  const blockType = normalizedText(block.blockType) ?? 'UNKNOWN';
  const displayStyle = normalizedText(block.displayStyle) ?? 'DEFAULT';
  const title = normalizedText(block.title) ?? '';
  return `${blockType}\u001f${displayStyle}\u001f${title}`;
}

function normalizedText(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim();
  return normalized || undefined;
}
